import { createPinia } from 'pinia';
import { createApp, watch } from 'vue';
import { fmtNumber, i18n, initialLanguage, setLanguage } from './i18n';
import App from './App.vue';
import { setSessionExpiredHandler } from './api/http';
import { router } from './router';
import { installStaleBuildRecovery, isStaleChunkError } from './utils/stale-build';
import { bootError } from './utils/boot-error';
import { useAuthStore } from './stores/auth.store';
import './style.css';
import './utils/theme'; // applies the saved/OS theme and keeps following the OS
import { polyfill as touchDragDrop } from 'mobile-drag-drop';
import { scrollBehaviourDragImageTranslateOverride } from 'mobile-drag-drop/scroll-behaviour';
import 'mobile-drag-drop/default.css';

// Touch screens: phones have no HTML5 drag-and-drop, so a press-and-hold on a draggable (the ≡ handle, a
// timeline block, a library item) turns into the same dragstart/dragover/drop events the mouse fires. The page
// still scrolls normally; dragging near the edge scrolls it.
if (window.matchMedia?.('(pointer: coarse)').matches) {
  touchDragDrop({
    forceApply: true,
    holdToDrag: 250,
    dragImageTranslateOverride: scrollBehaviourDragImageTranslateOverride,
    // The library's own lookup reads event.composedPath(), which is empty once the hold delay has passed.
    tryFindDraggableTarget: (e: TouchEvent) => ((e.target as Element | null)?.closest?.('[draggable="true"]') as HTMLElement | null) ?? undefined,
  });
  // The polyfill follows the spec strictly: an element only becomes the drop target if its dragenter is cancelled
  // (desktop browsers are lenient). Accept every dragenter; each list's dragover still decides where a drop is allowed.
  document.addEventListener('dragenter', (e) => e.preventDefault());
  // iOS Safari: a non-passive touchmove listener is needed for the polyfill to stop the page scrolling mid-drag.
  window.addEventListener('touchmove', () => undefined, { passive: false });
}

const app = createApp(App).use(createPinia()).use(router).use(i18n);
// After a deploy, an open tab reloads itself onto the new build instead of pages silently failing to open.
installStaleBuildRecovery(router);
// No page shown yet and the first one failed to open: say so instead of a blank page (a stale build reloads itself).
router.onError((err: unknown) => {
  if (router.currentRoute.value.matched.length || isStaleChunkError(err)) return;
  bootError.value = err instanceof Error ? err.message : String(err);
});
/** `{{ $num(n) }}` in templates: a bare number in the UI locale (Arabic-Indic digits unless switched off). */
app.config.globalProperties.$num = (n: number | null | undefined) => (n == null ? '—' : fmtNumber(n));

// Refresh failed (revoked, expired, reused token) → clear client state and go to login.
setSessionExpiredHandler(() => {
  useAuthStore().expire();
  const current = router.currentRoute.value;
  if (!current.meta.public) void router.push({ name: 'login', query: { redirect: current.fullPath } });
});

// Phase 17.7: the signed-in person's language and digits win over this browser's last choice.
watch(
  () => [useAuthStore().user?.language, useAuthStore().user?.numerals] as const,
  ([lang, numerals]) => {
    if (lang) void setLanguage(lang, numerals ?? 'arab');
  },
);

void initialLanguage().finally(() => app.mount('#app'));
